import { Router, ActivatedRoute } from '@angular/router';
import { RestService } from './../../services/rest.service';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { BackButtonService } from 'src/app/services/back-button.service';
import { AccordionService } from 'src/app/services/accordion.service';
import { Subject, takeUntil } from 'rxjs';
import { ViewWillEnter } from '@ionic/angular';
import { ProcessingDetailsOrder } from './processing-details.model';
import { DialogService, SnackbarService } from '@bcl/design-system';
import {
  WeightRegistrationDialogComponent,
  WeightRegistrationData,
  WeightRegistrationResult,
} from './weight-registration-dialog/weight-registration-dialog.component';

const ACCORDION_GROUP_ID = 'processing-details';

interface SectionConfig {
  id: string;
  title: string;
}

@Component({
  selector: 'app-processing-details',
  templateUrl: './processing-details.component.html',
  styleUrls: ['./processing-details.component.scss'],
})
export class ProcessingDetailsComponent
  implements OnInit, OnDestroy, ViewWillEnter
{
  public orderId: number;
  public processingLine: string | null = null;
  public order: ProcessingDetailsOrder | null = null;
  public loading = false;

  // Accordion sections configuration
  public sections: SectionConfig[] = [
    { id: 'inputOverview', title: 'Input Overview' },
    { id: 'outputOverview', title: 'Output Overview' },
  ];

  // Track open state for each section
  public sectionStates: Map<string, boolean> = new Map();

  // Track open state for quality sheet sections
  public qualitySheetStates: Map<number, boolean> = new Map();

  private destroyed$ = new Subject<void>();

  constructor(
    private rest: RestService,
    private router: Router,
    private route: ActivatedRoute,
    private backButtonService: BackButtonService,
    private accordionService: AccordionService,
    private dialog: DialogService,
    private snackbarService: SnackbarService
  ) {
    this.orderId = +this.route.snapshot.paramMap.get('id');
    this.processingLine = this.route.snapshot.queryParamMap.get('line');
  }

  ngOnInit() {
    // Register all sections with the accordion service
    this.sections.forEach((section, index) => {
      const sectionId = this.getSectionId(section.id);
      // First section opens by default
      const initialState = index === 0;

      this.accordionService
        .registerItem(sectionId, ACCORDION_GROUP_ID, initialState)
        .pipe(takeUntil(this.destroyed$))
        .subscribe((item) => {
          this.sectionStates.set(section.id, item.isOpen);
        });
    });
  }

  ngOnDestroy(): void {
    // Unregister all sections from the accordion service
    this.sections.forEach((section) => {
      this.accordionService.unregisterItem(this.getSectionId(section.id));
    });

    this.destroyed$.next();
    this.destroyed$.complete();
  }

  ionViewWillEnter(): void {
    this.setupBackButton();
    this.getData();
  }

  getData() {
    this.loading = true;
    this.rest
      .getOrderProcessingDetails(this.orderId, this.processingLine || '')
      .then((response: any) => {
        if (response) {
          const orderData = Array.isArray(response) ? response[0] : response;
          this.order = {
            // Header
            orderNumber: orderData.orderNumber || '',
            bio: orderData.bio || false,

            // Input Overview
            principal: orderData.principal || '',
            colli: orderData.colli || 0,
            nett: orderData.nett || 0,
            processingType: orderData.processingType || '',
            additionalInstructions: orderData.additionalInstructions || '',
            commodity: orderData.commodity || '',

            // Output Overview
            outputCommodity: orderData.outputCommodity || '',
            outgoingPallets: orderData.outgoingPallets || '',
            palletsAmount: orderData.palletsAmount || 0,
            palletCapacity: orderData.palletCapacity || 0,
            outgoingPackaging: orderData.outgoingPackaging || '',
            packagingAmount: orderData.packagingAmount || 0,
            outgoingPackaging2: orderData.outgoingPackaging2 || '',
            registeredWeightNett: orderData.registeredWeightNett || 0,
            registeredWasteNett: orderData.registeredWasteNett || 0,
            packingMaterialsLotNumber:
              orderData.packingMaterialsLotNumber || null,

            // Processing status
            processingStatus: orderData.processingStatus || 'Not started',
            blocked: orderData.blocked || false,

            weightRegistrations: orderData.weightRegistrations || [],
            wasteRegistrations: orderData.wasteRegistrations || [],
            qualitySheets: orderData.qualitySheets || [],
          };
        }
        this.loading = false;
      });
  }

  toggleSection(sectionId: string): void {
    this.accordionService.toggleItem(this.getSectionId(sectionId));
  }

  isSectionOpen(sectionId: string): boolean {
    return this.sectionStates.get(sectionId) || false;
  }

  public goBack() {
    this.router.navigate(['processing-sheet', this.orderId], {
      queryParams: { line: this.processingLine },
    });
  }

  public async startProcessing(): Promise<void> {
    try {
      await this.rest.updateProcessingStatus(
        this.orderId,
        this.processingLine || '',
        'start'
      );

      const initialSheet = this.order?.qualitySheets?.find(
        (sheet) => sheet.initial
      );

      if (initialSheet) {
        this.navigateToInitialQualitySheet(initialSheet);
      } else {
        this.getData();
      }
    } catch (error) {
      console.error('Error starting processing:', error);
      this.snackbarService.open(
        'red',
        'PROCESSING_DETAILS.MESSAGES.PROCESSING_STATUS_ERROR'
      );
    }
  }

  public async pauseProcessing(): Promise<void> {
    try {
      await this.rest.updateProcessingStatus(
        this.orderId,
        this.processingLine || '',
        'pause'
      );
      this.getData();
    } catch (error) {
      console.error('Error pausing processing:', error);
      this.snackbarService.open(
        'red',
        'PROCESSING_DETAILS.MESSAGES.PROCESSING_STATUS_ERROR'
      );
    }
  }

  public async resumeProcessing(): Promise<void> {
    try {
      await this.rest.updateProcessingStatus(
        this.orderId,
        this.processingLine || '',
        'resume'
      );
      this.getData();
    } catch (error) {
      console.error('Error resuming processing:', error);
      this.snackbarService.open(
        'red',
        'PROCESSING_DETAILS.MESSAGES.PROCESSING_STATUS_ERROR'
      );
    }
  }

  public async finalizeProcessing(): Promise<void> {
    try {
      await this.rest.updateProcessingStatus(
        this.orderId,
        this.processingLine || '',
        'finalized'
      );
      this.getData();
    } catch (error) {
      console.error('Error finalizing processing:', error);
      this.snackbarService.open(
        'red',
        'PROCESSING_DETAILS.MESSAGES.PROCESSING_STATUS_ERROR'
      );
    }
  }

  public openWeightRegistration(): void {
    const dialogRef = this.dialog.open<
      WeightRegistrationDialogComponent,
      WeightRegistrationData,
      WeightRegistrationResult
    >(WeightRegistrationDialogComponent, {
      minWidth: '90vw',
      data: {
        outgoingPackaging: this.order?.outgoingPackaging || '',
        existingWeightRegistrations: this.order?.weightRegistrations || [],
        palletsAmount: this.order?.palletsAmount || 0,
        packagingAmount: this.order?.packagingAmount || 0,
        palletCapacity: this.order?.palletCapacity || 0,
        packingMaterialsLotNumber:
          this.order?.packingMaterialsLotNumber || null,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.rest
          .saveWeightRegistration(
            this.orderId,
            this.processingLine || '',
            result
          )
          .then(() => {
            this.snackbarService.open(
              'green',
              'PROCESSING_DETAILS.MESSAGES.WEIGHT_REGISTRATION_SAVED'
            );
            this.getData();
          })
          .catch(() => {
            this.snackbarService.open(
              'red',
              'PROCESSING_DETAILS.MESSAGES.WEIGHT_REGISTRATION_ERROR'
            );
          });
      }
    });
  }

  public openWasteRegistration(): void {
    this.router.navigate(['waste-registration', this.orderId], {
      queryParams: { line: this.processingLine },
    });
  }

  public getWasteSourceLabel(source: string): string {
    const sourceLabels: { [key: string]: string } = {
      sieve: 'WASTE_REGISTRATION.OPTIONS.SIEVE',
      laser: 'WASTE_REGISTRATION.OPTIONS.LASER',
      aspiratorCyclone: 'WASTE_REGISTRATION.OPTIONS.ASPIRATOR_CYCLONE',
      xray: 'WASTE_REGISTRATION.OPTIONS.XRAY',
      sweepings: 'WASTE_REGISTRATION.OPTIONS.SWEEPINGS',
      other: 'WASTE_REGISTRATION.OPTIONS.OTHER',
    };
    return sourceLabels[source] || source;
  }

  public toggleQualitySheet(index: number): void {
    const currentState = this.qualitySheetStates.get(index) || false;
    this.qualitySheetStates.set(index, !currentState);
  }

  public isQualitySheetOpen(index: number): boolean {
    return this.qualitySheetStates.get(index) || false;
  }

  public hasFilledQuestionnaires(sheet: any): boolean {
    return sheet.questionnaires && sheet.questionnaires.length > 0;
  }

  public onQualitySheetAdd(sheet: any, event: Event): void {
    event.stopPropagation();
    if (this.order?.blocked) {
      return;
    }
    const backUrl = this.processingLine
      ? `/processing-details/${this.orderId}?line=${this.processingLine}`
      : `/processing-details/${this.orderId}`;

    this.router.navigate(
      ['questionnaire', sheet.customFormId, 'ProcessingDetails', this.orderId],
      {
        state: {
          backUrl,
          questionnaireId: null,
          editable: true,
        },
      }
    );
  }

  public onQualitySheetView(
    questionnaire: any,
    sheet: any,
    event: Event
  ): void {
    event.stopPropagation();
    const backUrl = this.processingLine
      ? `/processing-details/${this.orderId}?line=${this.processingLine}`
      : `/processing-details/${this.orderId}`;

    this.router.navigate(
      ['questionnaire', sheet.customFormId, 'ProcessingDetails', this.orderId],
      {
        state: {
          backUrl,
          questionnaireId: questionnaire.questionnaireId,
          editable:
            this.order?.blocked ||
            this.order?.processingStatus !== 'In progress'
              ? false
              : sheet.editable ?? false,
        },
      }
    );
  }

  private setupBackButton(): void {
    this.backButtonService.showBackButton({
      show: true,
      action: () => {
        this.goBack();
      },
    });
  }

  private getSectionId(sectionId: string): string {
    return `${ACCORDION_GROUP_ID}-${sectionId}`;
  }

  private navigateToInitialQualitySheet(sheet: {
    customFormId: number;
    editable: boolean;
  }): void {
    const backUrl = this.processingLine
      ? `/processing-details/${this.orderId}?line=${this.processingLine}`
      : `/processing-details/${this.orderId}`;

    this.router.navigate(
      ['questionnaire', sheet.customFormId, 'ProcessingDetails', this.orderId],
      {
        state: {
          backUrl,
          questionnaireId: null,
          editable: true,
        },
      }
    );
  }
}
